import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, Row, Col, Spin } from 'antd';
import { OnlineInventoryAPI } from '../../api/onlineInventoryAPI';
import { CustomText, FormInput, FormSelect, useCustomToast } from '../../../../components';
import { useAtom } from 'jotai';
import { authAtom } from '../../../../state';
import { InventoryViewAPI } from '../../../InventoryView/api/inventoryViewAPI';

interface FisicalLocationViewProps {
  visible: boolean;
  onClose: () => void;
  record: any;
  refetch?: () => void;
}

const FisicalLocationView: React.FC<FisicalLocationViewProps> = ({
  visible,
  onClose,
  record,
  refetch,
}) => {
  const { showToast } = useCustomToast();
  const [form] = Form.useForm();
  const [locations, setLocations] = useState<any[]>(
    []
  );
  const [cities, setCities] = useState<{ id: number; name: string }[]>([]);
  const [departments, setDepartments] = useState<
    { id: number; name: string }[]
  >([]);
  const [authData] = useAtom(authAtom);
  const [branches, setBranches] = useState<{ id: number; name: string }[]>([]);
  const [isFormValid, setIsFormValid] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isEditable, setIsEditable] = useState(false);

  useEffect(() => {
    if (!visible || isLoading || !!record?.selectedRowKeys) return;

    setIsLoading(true);
    OnlineInventoryAPI.showVerification(record.inventory_id, record.id)
      .then(response => {
        const new_location = response?.new_location_info || {};
        setIsEditable(!!new_location.id);
        form.setFieldsValue({
          Buscador: String(new_location.id || ''),
          code: new_location.code,
          name: new_location.name,
          type: new_location.type,
          branch_id: String(new_location.branch_id || ''),
          department_id: new_location.department_id,
          city_id: new_location.city_id,
          parent_location_id: new_location.parent_location_id,
          address: new_location.address,
          area: new_location.area,
          floor: new_location.floor,
        });
      })
      .catch(error => {
        setIsEditable(false);
      })
      .finally(() => setIsLoading(false));
  }, [visible, record]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const { locations } = await InventoryViewAPI.fetchParameters(
          ['locations'],
          { branch_id: String(authData?.user?.branch_id) }
        );

        const data = await OnlineInventoryAPI.fetchParameters([
          'cities',
          'departments',
          'branches',
        ]);

        if (data) {
          setLocations(locations || []);
          setCities(data.cities || []);
          setDepartments(data.departments || []);
          setBranches(data.branches || []);
        }
      } catch (error) {
        console.error('Error fetching parameters:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleLocationData = (id: number) => {
    const location = locations.find((loc) => loc.id === id);

    if (!location) return;
    form.setFieldsValue({
      code: location.code,
      name: location.name,
      type: location.type,
      branch_id: String(location.branch_id || ''),
      department_id: location.department_id,
      city_id: location.city_id,
      parent_location_id: location.parent_location_id,
      address: location.address,
      area: location.area,
      floor: location.floor,
    });
  }

  const handleSave = () => {
    form.validateFields().then(async (values) => {
      if (!record) {
        showToast('error', 'No se ha seleccionado un registro válido');
        return;
      }

      const assets =
        Array.isArray(record?.selectedRowKeys)
          ? record.selectedRowKeys
          : [record?.id];

      const locationData = {
        inventory_id: record.inventory_id,
        new_location_id: values.Buscador,
        assets
      };

      try {
        const response = await OnlineInventoryAPI.updateAssetLocations(locationData);
        showToast('success', response?.message || 'Ubicación actualizada correctamente');
        if (refetch) refetch();
        onClose();
      } catch (error: any) {
        showToast(
          'error',
          error?.response?.data?.message ||
          'Error al actualizar la ubicación'
        );
      }
    });
  };

  return (
    <Modal
      title="Solicitud de cambio de ubicación física del bien"
      open={visible}
      onCancel={onClose}
      footer={null}
      width={800}
    >
      {loading ? (
        <Row justify="center" align="middle" style={{ minHeight: 200 }}>
          <Spin size="large" />
        </Row>
      ) : (
        <Form
          layout="vertical"
          form={form}
          onValuesChange={() => {
            const values = form.getFieldsValue([
              'Buscador',
              'code',
              'name',
              'type',
              'branch_id',
              'department_id',
              'city_id',
              'parent_location_id',
            ]);
            setIsFormValid(
              !!values.Buscador
            );
          }}
        >
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="Buscador">
                <FormSelect
                  disabled={isEditable}
                  label="Buscador"
                  name="ubicacionPadre"
                  placeholder='Código/nombre de ubicación'
                  value=""
                  options={locations?.map((x) => ({
                    name: x.name,
                    id: x.id.toString(),
                  }))}
                  onChange={(value) => handleLocationData(Number(value))}
                  onBlur={() => {}}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="code">
                <FormInput
                  label="Código de ubicación*"
                  name="code"
                  value=""
                  onChange={() => {}}
                  onBlur={() => {}}
                  disabled
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="name">
                <FormInput
                  label="Nombre"
                  name="name"
                  value=""
                  onChange={() => {}}
                  onBlur={() => {}}
                  disabled
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="address">
                <FormInput
                  label="Dirección(Detalle)*"
                  name="address"
                  value=""
                  onChange={() => {}}
                  onBlur={() => {}}
                  disabled
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="type">
                <FormSelect
                  label="Tipo de ubicación*"
                  name="type"
                  value=""
                  placeholder=''
                  options={[
                    { name: 'Sede', id: 'Sede' },
                    { name: 'Piso', id: 'Piso' },
                    { name: 'Área', id: 'Área' },
                    { name: 'Seccional', id: 'Seccional' },
                    { name: 'Regional', id: 'Regional' },
                  ]}
                  onChange={() => {}}
                  onBlur={() => {}}
                  disabled
                />
              </Form.Item>
            </Col>
             <Col span={8}>
              <Form.Item name="branch_id">
                <FormSelect
                  label="Seccional*"
                  name="branch_id"
                  value=""
                  placeholder=''
                  options={branches?.map((x) => ({
                    name: x.name,
                    id: x.id.toString(),
                  }))}
                  onChange={() => {}}
                  onBlur={() => {}}
                  disabled
                />
              </Form.Item>
            </Col>
          </Row>

          <CustomText
            text='Territorialidad'
            fontSize="small"
            strong
          />
          <hr style={{ margin: '8px 0', opacity: 0.5 }} />

          <Row gutter={16} className='mt-4'>
            <Col span={8}>
              <Form.Item name="department_id">
                <FormSelect
                  label="Departamento*"
                  name="department_id"
                  placeholder=''
                  value=""
                  options={departments?.map((x) => ({
                    name: x.name,
                    id: x.id.toString(),
                  }))}
                  onChange={() => {}}
                  onBlur={() => {}}
                  disabled
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="city_id">
                <FormSelect
                  label="Ciudad*"
                  name="city_id"
                  placeholder=''
                  value=""
                  options={cities?.map((x) => ({
                    name: x.name,
                    id: x.id.toString(),
                  }))}
                  onChange={() => {}}
                  onBlur={() => {}}
                  disabled
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="parent_location_id">
                <FormSelect
                  label="Ubicación padre"
                  name="parent_location_id"
                  placeholder=''
                  value=""
                  options={locations?.map((x) => ({
                    name: x.name,
                    id: x.id.toString(),
                  }))}
                  onChange={() => {}}
                  onBlur={() => {}}
                  disabled
                />
              </Form.Item>
            </Col>
          </Row>

          <CustomText
            text='Datos adicionales'
            fontSize="small"
            strong
          />
          <hr style={{ margin: '8px 0', opacity: 0.5 }} />

          <Row gutter={16} className='mt-4'>
            <Col span={8}>
              <Form.Item name="area">
                <FormInput
                  label="Área (Opcional)"
                  name="area"
                  value=""
                  onChange={() => {}}
                  onBlur={() => {}}
                  disabled
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="floor">
                <FormInput
                  label="Piso (Opcional)"
                  name="floor"
                  value=""
                  onChange={() => {}}
                  onBlur={() => {}}
                  disabled
                />
              </Form.Item>
            </Col>
          </Row>

          <Row justify="end" gutter={16}>
            <Col>
              <Button onClick={onClose}>Cancelar</Button>
            </Col>
            <Col>
               {!isEditable ?
                 <Button
                  type="primary"
                  onClick={handleSave}
                  disabled={!isFormValid}
                >
                  Guardar
                </Button> :
                <Button type="primary" onClick={() => setIsEditable(false)}>
                  Editar
                </Button>}
            </Col>
          </Row>
        </Form>
      )}
    </Modal>
  );
};

export default FisicalLocationView;